import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Calendar, DollarSign, User, FileText, Target, Globe, Users, Search, Sparkles, Loader2, Filter, X } from 'lucide-react';
import { useBrandDeals } from '@/hooks/useBrandDeals';
import { useToast } from '@/hooks/use-toast';
import { userService } from '@/services/userService';
import { DealAIGenerationModal } from '@/components/brand/DealAIGenerationModal';
import { EligibilityCriteria } from '@/hooks/useMarketplaceDeals';
import { useEnhancedMobile } from '@/hooks/use-enhanced-mobile';
import EnhancedTalentMarketplace from '@/components/venture/EnhancedTalentMarketplace';
import { pickSafeFields, mapSupabaseError } from '@/utils/security';

const CreateDealPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useEnhancedMobile();
  const { createDeal } = useBrandDeals();
  const { toast } = useToast();
  const [athletes, setAthletes] = useState<any[]>([]);
  const [showAIGeneration, setShowAIGeneration] = useState(false);
  const [showMarketplaceBrowser, setShowMarketplaceBrowser] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deal_type: '',
    deal_value: '',
    talent_profile_id: '',
    estimated_completion_date: '',
    priority: 'medium',
    status: 'pending',
    posting_type: '',
    requirements: '',
    target_criteria: ''
  });

  const [eligibilityCriteria, setEligibilityCriteria] = useState<EligibilityCriteria>({});
  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);
  const [selectedCompLevels, setSelectedCompLevels] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedAthleteInfo, setSelectedAthleteInfo] = useState<any>(null);

  // Fetch athletes on mount
  useEffect(() => {
    const fetchAthletes = async () => {
      try {
        const athleteProfiles = await userService.getTalents();
        setAthletes(athleteProfiles);
      } catch (error) {
        console.error('Failed to fetch athletes:', error);
      }
    };
    fetchAthletes();
  }, []);

  // Handle pre-populated deal from navigation state
  useEffect(() => {
    const prePopulatedDeal = location.state?.prePopulatedDeal;
    if (prePopulatedDeal) {
      setFormData({
        title: prePopulatedDeal.title || '',
        description: prePopulatedDeal.description || '',
        deal_type: prePopulatedDeal.deal_type || 'collaboration',
        deal_value: prePopulatedDeal.deal_value?.toString() || '1000',
        talent_profile_id: '',
        estimated_completion_date: '',
        priority: prePopulatedDeal.priority || 'medium',
        status: 'available',
        posting_type: 'marketplace',
        requirements: prePopulatedDeal.requirements || '',
        target_criteria: prePopulatedDeal.target_criteria || ''
      });
      setCurrentStep(1);
      toast({
        title: "Deal Pre-populated",
        description: "Your deal has been pre-populated with the provided content.",
      });
    }
  }, [location.state, toast]);

  // Handle pending deal from sessionStorage (AI chat flow). sessionStorage
  // can be written by any script on this origin, so we extract ONLY the
  // fields we expect and coerce each to a safe type. Nothing from the
  // parsed object is ever spread into state.
  useEffect(() => {
    const pendingDeal = sessionStorage.getItem('pendingDealCreation');
    if (!pendingDeal) return;

    const asString = (v: unknown, max = 2000) =>
      typeof v === 'string' ? v.slice(0, max) : '';
    const asEnum = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T =>
      typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;

    const dealData = pickSafeFields(pendingDeal, {
      title: (v) => asString(v, 200),
      description: (v) => asString(v, 2000),
      deal_type: (v) => asEnum(v, ['collaboration', 'sponsorship', 'ambassador', 'event'] as const, 'collaboration'),
      deal_value: (v) => {
        if (typeof v === 'number' && isFinite(v) && v >= 0) return String(Math.floor(v));
        if (typeof v === 'string' && /^\d+(\.\d+)?$/.test(v)) return v;
        return '1000';
      },
      priority: (v) => asEnum(v, ['low', 'medium', 'high'] as const, 'medium'),
      requirements: (v) => asString(v, 2000),
      target_criteria: (v) => asString(v, 2000),
    });

    if (!dealData) {
      sessionStorage.removeItem('pendingDealCreation');
      return;
    }

    setFormData({
      title: dealData.title,
      description: dealData.description,
      deal_type: dealData.deal_type,
      deal_value: dealData.deal_value,
      talent_profile_id: '',
      estimated_completion_date: '',
      priority: dealData.priority,
      status: 'available',
      posting_type: 'marketplace',
      requirements: dealData.requirements,
      target_criteria: dealData.target_criteria,
    });
    setCurrentStep(1);
    sessionStorage.removeItem('pendingDealCreation');
    toast({
      title: 'AI Deal Generated',
      description: 'Your deal has been pre-populated with AI-generated content.',
    });
  }, [toast]);

  const steps = [
    { id: 'athlete', title: 'Select Athlete', icon: User },
    { id: 'details', title: 'Deal Details', icon: FileText },
    { id: 'terms', title: 'Terms & Value', icon: DollarSign },
    { id: 'timeline', title: 'Timeline', icon: Calendar },
  ];

  const dealTypes = [
    { value: 'sponsorship', label: 'Sponsorship', description: 'Brand sponsorship agreement' },
    { value: 'collaboration', label: 'Collaboration', description: 'Content collaboration' },
    { value: 'ambassador', label: 'Brand Ambassador', description: 'Long-term ambassador program' },
    { value: 'event', label: 'Event Partnership', description: 'Event appearance or participation' },
  ];

  const handleSubmit = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const dealData: any = {
        ...formData,
        deal_value: parseFloat(formData.deal_value) || 0,
      };

      if (formData.posting_type === 'marketplace') {
        dealData.talent_profile_id = null;
        dealData.status = 'available';
        const criteria: EligibilityCriteria = {};
        if (eligibilityCriteria.min_followers) criteria.min_followers = eligibilityCriteria.min_followers;
        if (eligibilityCriteria.max_followers) criteria.max_followers = eligibilityCriteria.max_followers;
        if (selectedSports.length > 0) criteria.sports = selectedSports;
        if (selectedTiers.length > 0) criteria.tiers = selectedTiers;
        if (selectedCompLevels.length > 0) criteria.competition_levels = selectedCompLevels;
        if (eligibilityCriteria.min_age) criteria.min_age = eligibilityCriteria.min_age;
        if (eligibilityCriteria.max_age) criteria.max_age = eligibilityCriteria.max_age;
        if (eligibilityCriteria.verified_only) criteria.verified_only = true;
        dealData.eligibility_criteria = criteria;
      }

      await createDeal(dealData);
      toast({
        title: "Deal Created",
        description: formData.posting_type === 'marketplace'
          ? "Your deal has been posted to the marketplace successfully."
          : "Your deal has been created successfully.",
      });
      navigate('/brand-dashboard');
    } catch (error) {
      // Surface user-safe DB errors verbatim (e.g. wallet-funding guard
      // reject) and fall back to a generic message for anything else.
      toast({
        title: "Error",
        description: mapSupabaseError(error, "Failed to create deal. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const nextStep = () => setCurrentStep(Math.min(currentStep + 1, steps.length - 1));
  const prevStep = () => setCurrentStep(Math.max(currentStep - 1, 0));

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return formData.posting_type !== '' &&
               (formData.posting_type === 'marketplace' || formData.talent_profile_id !== '');
      case 1: return formData.title !== '' && formData.deal_type !== '';
      case 2: return formData.deal_value !== '';
      case 3: return true;
      default: return false;
    }
  };

  const selectedAthlete = athletes.find(a => a.id === formData.talent_profile_id);

  const handleMarketplaceAthleteSelect = (athlete: any) => {
    // Set selected athlete info for display
    setSelectedAthleteInfo({
      name: athlete.display_name || athlete.name || athlete.primary_name,
      sport: athlete.sport || athlete.platform || 'Creator',
      team: athlete.current_team_name,
      photo: athlete.headshot_url || athlete.avatar_url,
    });
    
    // If the athlete has a claimed profile, use that
    if (athlete.claimed_by_profile_id) {
      setFormData({ ...formData, talent_profile_id: athlete.claimed_by_profile_id, posting_type: 'specific' });
    } else {
      // For unclaimed athletes, pre-populate deal info and use marketplace posting
      setFormData({
        ...formData,
        posting_type: 'marketplace',
        target_criteria: `${athlete.display_name || athlete.name || athlete.primary_name} - ${athlete.sport || athlete.platform || 'Creator'}`,
        title: formData.title || `Partnership with ${athlete.display_name || athlete.name || athlete.primary_name}`,
        description: formData.description || `Deal with ${athlete.display_name || athlete.name || athlete.primary_name}${athlete.current_team_name ? ` (${athlete.current_team_name})` : ''}`,
      });
    }
    setShowMarketplaceBrowser(false);
  };

  const handleAthleteSelection = (athlete: any) => {
    setFormData({ ...formData, talent_profile_id: athlete.id, posting_type: 'specific' });
  };

  const handleAIGeneration = (generatedData: any) => {
    setFormData({
      ...formData,
      title: generatedData.title || formData.title,
      description: generatedData.description || formData.description,
      deal_type: generatedData.deal_type || formData.deal_type,
      priority: generatedData.priority || formData.priority,
      requirements: generatedData.requirements || formData.requirements,
      target_criteria: generatedData.target_criteria || formData.target_criteria,
      posting_type: generatedData.posting_type || 'marketplace',
      talent_profile_id: generatedData.talent_profile_id || '',
      status: generatedData.status || 'available',
      deal_value: String(generatedData.deal_value || 0),
      estimated_completion_date: generatedData.estimated_completion_date || formData.estimated_completion_date,
    });
    setCurrentStep(1);
    toast({
      title: "AI Generation Applied",
      description: "Deal details have been populated with AI-generated content.",
    });
  };

  // If browsing marketplace, show the full marketplace inline
  if (showMarketplaceBrowser) {
    return (
      <Layout>
        <PageHeader title="Talent Marketplace." subtitle="Discover and analyze talent partnerships" />
        <div className={`container mx-auto max-w-[1200px] overflow-x-hidden ${isMobile ? 'px-2 py-2' : 'px-6 py-6'}`}>
          <Button
            variant="ghost"
            onClick={() => setShowMarketplaceBrowser(false)}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Deal Creation
          </Button>
          <EnhancedTalentMarketplace onSelectForDeal={handleMarketplaceAthleteSelect} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader title="Create New Deal." subtitle="Set up a new partnership deal" />
      <div className={`container mx-auto max-w-[1200px] overflow-x-hidden ${isMobile ? 'px-2 py-2' : 'px-6 py-6'}`}>
        <Button
          variant="ghost"
          onClick={() => navigate('/brand-dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    isActive ? 'bg-primary text-primary-foreground' :
                    isCompleted ? 'bg-success text-success-foreground' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className={`ml-2 text-sm ${
                    isActive ? 'text-foreground font-medium' : 'text-muted-foreground'
                  }`}>
                    {step.title}
                  </span>
                  {index < steps.length - 1 && (
                    <div className={`mx-4 h-px w-8 ${
                      isCompleted ? 'bg-success' : 'bg-muted'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Step Content */}
          <div className="min-h-[400px]">
            {currentStep === 0 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Choose Deal Posting Method</h3>
                  <p className="text-sm text-muted-foreground">
                    Select how you'd like to create this deal opportunity
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Marketplace Option */}
                  <Card
                    className={`cursor-pointer transition-all ${
                      formData.posting_type === 'marketplace'
                        ? 'ring-2 ring-primary bg-primary/5'
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => setFormData({ ...formData, posting_type: 'marketplace', talent_profile_id: '' })}
                  >
                    <CardHeader className="text-center">
                      <Globe className="w-8 h-8 mx-auto mb-2 text-primary" />
                      <CardTitle className="text-sm">Post to Marketplace</CardTitle>
                      <CardDescription className="text-xs">
                        Make this deal available for any athlete to claim
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>• Reach a wider audience</p>
                        <p>• Athletes can self-select</p>
                        <p>• Faster discovery</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Search Marketplace Option */}
                  <Card
                    className={`cursor-pointer transition-all ${
                      formData.posting_type === 'directory'
                        ? 'ring-2 ring-primary bg-primary/5'
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => {
                      setFormData({ ...formData, posting_type: 'directory' });
                      setShowMarketplaceBrowser(true);
                    }}
                  >
                    <CardHeader className="text-center">
                      <Search className="w-8 h-8 mx-auto mb-2 text-primary" />
                      <CardTitle className="text-sm">Search Marketplace</CardTitle>
                      <CardDescription className="text-xs">
                        Browse the talent marketplace to find athletes and creators
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>• Advanced search filters</p>
                        <p>• Detailed athlete profiles</p>
                        <p>• Performance metrics</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Quick Select Option */}
                  <Card
                    className={`cursor-pointer transition-all ${
                      formData.posting_type === 'quick'
                        ? 'ring-2 ring-primary bg-primary/5'
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => setFormData({ ...formData, posting_type: 'quick' })}
                  >
                    <CardHeader className="text-center">
                      <Users className="w-8 h-8 mx-auto mb-2 text-primary" />
                      <CardTitle className="text-sm">Quick Select</CardTitle>
                      <CardDescription className="text-xs">
                        Choose from recently viewed athletes
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>• Simple selection</p>
                        <p>• Recently active athletes</p>
                        <p>• Fast deal creation</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Quick select athlete list */}
                {formData.posting_type === 'quick' && (
                  <div className="space-y-4">
                    <h4 className="font-medium">Select an Athlete</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                      {athletes.slice(0, 8).map((athlete) => (
                        <Card
                          key={athlete.id}
                          className={`cursor-pointer transition-all ${
                            formData.talent_profile_id === athlete.id
                              ? 'ring-2 ring-primary bg-primary/5'
                              : 'hover:shadow-md'
                          }`}
                          onClick={() => handleAthleteSelection(athlete)}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-center space-x-3">
                              {athlete.photo && (
                                <img src={athlete.photo} alt={athlete.name} className="w-10 h-10 rounded-full object-cover" />
                              )}
                              <div className="flex-1">
                                <p className="text-sm font-medium">{athlete.name}</p>
                                <p className="text-xs text-muted-foreground">{athlete.sport}</p>
                              </div>
                              <Badge variant="outline" className="text-xs">{athlete.tier}</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Marketplace eligibility criteria */}
                {formData.posting_type === 'marketplace' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Filter className="h-4 w-4" />
                      <h4 className="font-medium">Eligibility Criteria</h4>
                      <Badge variant="secondary" className="text-xs">Optional</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Set criteria to show this deal only to qualified talents. Leave blank for no restrictions.
                    </p>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm">Min Followers</Label>
                          <Input type="number" placeholder="e.g., 10000" value={eligibilityCriteria.min_followers || ''} onChange={(e) => setEligibilityCriteria({ ...eligibilityCriteria, min_followers: e.target.value ? parseInt(e.target.value) : undefined })} />
                        </div>
                        <div>
                          <Label className="text-sm">Max Followers</Label>
                          <Input type="number" placeholder="e.g., 100000" value={eligibilityCriteria.max_followers || ''} onChange={(e) => setEligibilityCriteria({ ...eligibilityCriteria, max_followers: e.target.value ? parseInt(e.target.value) : undefined })} />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm mb-2 block">Sports</Label>
                        <div className="flex flex-wrap gap-2">
                          {['Football', 'Basketball', 'Baseball', 'Soccer', 'Tennis', 'Golf', 'Hockey', 'Track & Field'].map(sport => (
                            <Badge key={sport} variant={selectedSports.includes(sport) ? "default" : "outline"} className="cursor-pointer" onClick={() => setSelectedSports(prev => prev.includes(sport) ? prev.filter(s => s !== sport) : [...prev, sport])}>
                              {sport}
                              {selectedSports.includes(sport) && <X className="ml-1 h-3 w-3" />}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm mb-2 block">Tier Levels</Label>
                        <div className="flex gap-2">
                          {['Silver', 'Gold', 'Platinum'].map(tier => (
                            <Badge key={tier} variant={selectedTiers.includes(tier) ? "default" : "outline"} className="cursor-pointer" onClick={() => setSelectedTiers(prev => prev.includes(tier) ? prev.filter(t => t !== tier) : [...prev, tier])}>
                              {tier}
                              {selectedTiers.includes(tier) && <X className="ml-1 h-3 w-3" />}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm mb-2 block">Competition Level</Label>
                        <div className="flex flex-wrap gap-2">
                          {['amateur', 'semi-pro', 'collegiate', 'professional'].map(level => (
                            <Badge key={level} variant={selectedCompLevels.includes(level) ? "default" : "outline"} className="cursor-pointer capitalize" onClick={() => setSelectedCompLevels(prev => prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level])}>
                              {level}
                              {selectedCompLevels.includes(level) && <X className="ml-1 h-3 w-3" />}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm">Min Age</Label>
                          <Input type="number" placeholder="e.g., 18" value={eligibilityCriteria.min_age || ''} onChange={(e) => setEligibilityCriteria({ ...eligibilityCriteria, min_age: e.target.value ? parseInt(e.target.value) : undefined })} />
                        </div>
                        <div>
                          <Label className="text-sm">Max Age</Label>
                          <Input type="number" placeholder="e.g., 30" value={eligibilityCriteria.max_age || ''} onChange={(e) => setEligibilityCriteria({ ...eligibilityCriteria, max_age: e.target.value ? parseInt(e.target.value) : undefined })} />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm">Verified Profiles Only</Label>
                          <p className="text-xs text-muted-foreground">Only show to verified athletes</p>
                        </div>
                        <Switch checked={eligibilityCriteria.verified_only || false} onCheckedChange={(checked) => setEligibilityCriteria({ ...eligibilityCriteria, verified_only: checked })} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Selected from marketplace display */}
                {(formData.posting_type === 'directory' || selectedAthleteInfo) && selectedAthleteInfo && (
                  <Card className="bg-primary/5 border-primary">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Selected from Marketplace</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center space-x-3">
                        {selectedAthleteInfo.photo && (
                          <img src={selectedAthleteInfo.photo} alt={selectedAthleteInfo.name} className="w-10 h-10 rounded-full object-cover" />
                        )}
                        <div>
                          <p className="font-medium">{selectedAthleteInfo.name}</p>
                          <p className="text-sm text-muted-foreground">{selectedAthleteInfo.sport}{selectedAthleteInfo.team ? ` • ${selectedAthleteInfo.team}` : ''}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setShowMarketplaceBrowser(true)}>
                          Change
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Deal Details</h3>
                  <Button variant="outline" onClick={() => setShowAIGeneration(true)} className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Generate with AI
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Deal Title</Label>
                    <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="e.g., Spring Collection Campaign" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Describe the partnership goals and expectations..." rows={3} />
                </div>
                <div className="space-y-2">
                  <Label>Deal Type</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {dealTypes.map((type) => (
                      <Card key={type.value} className={`cursor-pointer transition-all ${formData.deal_type === type.value ? 'ring-2 ring-primary bg-primary/5' : 'hover:shadow-sm'}`} onClick={() => setFormData({ ...formData, deal_type: type.value })}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">{type.label}</CardTitle>
                          <CardDescription className="text-xs">{type.description}</CardDescription>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Terms & Value</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="deal_value">Deal Value (USD)</Label>
                    <Input id="deal_value" type="number" value={formData.deal_value} onChange={(e) => setFormData({ ...formData, deal_value: e.target.value })} placeholder="10000" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Initial Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.posting_type === 'marketplace' ? (
                  <Card>
                    <CardHeader><CardTitle className="text-sm">Marketplace Posting</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-sm">This deal will be posted to the marketplace for any athlete to claim.</p>
                        {formData.requirements && <div><p className="text-xs font-medium text-muted-foreground">Requirements:</p><p className="text-xs">{formData.requirements}</p></div>}
                        {formData.target_criteria && <div><p className="text-xs font-medium text-muted-foreground">Target Criteria:</p><p className="text-xs">{formData.target_criteria}</p></div>}
                      </div>
                    </CardContent>
                  </Card>
                ) : selectedAthlete ? (
                  <Card>
                    <CardHeader><CardTitle className="text-sm">Selected Athlete</CardTitle></CardHeader>
                    <CardContent>
                      <div className="flex items-center space-x-3">
                        {selectedAthlete.photo && <img src={selectedAthlete.photo} alt={selectedAthlete.name} className="w-10 h-10 rounded-full object-cover" />}
                        <div>
                          <p className="font-medium">{selectedAthlete.name}</p>
                          <p className="text-sm text-muted-foreground">{selectedAthlete.sport}</p>
                        </div>
                        <Badge>{selectedAthlete.tier}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ) : selectedAthleteInfo ? (
                  <Card>
                    <CardHeader><CardTitle className="text-sm">Selected from Marketplace</CardTitle></CardHeader>
                    <CardContent>
                      <div className="flex items-center space-x-3">
                        {selectedAthleteInfo.photo && <img src={selectedAthleteInfo.photo} alt={selectedAthleteInfo.name} className="w-10 h-10 rounded-full object-cover" />}
                        <div>
                          <p className="font-medium">{selectedAthleteInfo.name}</p>
                          <p className="text-sm text-muted-foreground">{selectedAthleteInfo.sport}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : null}
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Timeline & Completion</h3>
                <div className="space-y-2">
                  <Label htmlFor="estimated_completion_date">Estimated Completion Date</Label>
                  <Input id="estimated_completion_date" type="date" value={formData.estimated_completion_date} onChange={(e) => setFormData({ ...formData, estimated_completion_date: e.target.value })} />
                </div>
                <Card>
                  <CardHeader><CardTitle className="text-sm">Deal Summary</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between"><span>Title:</span><span className="font-medium">{formData.title}</span></div>
                    <div className="flex justify-between"><span>Type:</span><span className="font-medium">{formData.deal_type}</span></div>
                    <div className="flex justify-between"><span>Value:</span><span className="font-medium">${formData.deal_value}</span></div>
                    <div className="flex justify-between">
                      <span>Athlete:</span>
                      <span className="font-medium">
                        {formData.posting_type === 'marketplace'
                          ? 'Marketplace (Any Athlete)'
                          : selectedAthlete?.name || selectedAthleteInfo?.name || 'Not Selected'}
                      </span>
                    </div>
                    <div className="flex justify-between"><span>Priority:</span><Badge variant="outline">{formData.priority}</Badge></div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={prevStep} disabled={currentStep === 0}>
              Previous
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/brand-dashboard')}>
                Cancel
              </Button>
              {currentStep === steps.length - 1 ? (
                <Button onClick={handleSubmit} disabled={!isStepValid() || isCreating}>
                  {isCreating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>) : 'Create Deal'}
                </Button>
              ) : (
                <Button onClick={nextStep} disabled={!isStepValid()}>Next</Button>
              )}
            </div>
          </div>
        </div>

        {/* AI Generation Modal */}
        <DealAIGenerationModal
          isOpen={showAIGeneration}
          onClose={() => setShowAIGeneration(false)}
          onApplyGeneration={handleAIGeneration}
        />
      </div>
    </Layout>
  );
};

export default CreateDealPage;
